/**
 * Filtros de la PLP: recorte de valores por grupo, buscador dentro del grupo, modal en
 * mobile y filtrado en vivo por Section Rendering API. Las barras pegadas son solo CSS.
 *
 * El formulario `[data-faceted-filter-form]` es la unica fuente de verdad del estado:
 * los chips y los enlaces de limpiar destildan inputs en vez de navegar, y la peticion se
 * arma leyendo el formulario cuando vence el debounce, no cuando ocurre el evento. Por eso
 * poner y quitar el mismo filtro rapido termina en una sola peticion, y una respuesta que
 * llega tarde se descarta en vez de pisar lo que el cliente acaba de tocar.
 */
(() => {
  const form = document.querySelector('[data-faceted-filter-form]');
  if (!form) return;

  const toolbar = document.querySelector('[data-plp-toolbar]');
  const results = document.querySelector('[data-plp-results]');
  const sortEl = document.querySelector('[data-sort-by]');
  const sortLabel = document.querySelector('.ff-sort__value');
  const dialog = document.querySelector('[data-filter-dialog]');
  const sectionId = toolbar ? toolbar.dataset.sectionId : '';

  /* ---------------------------------------------------------------------------
     Recorte de valores y buscador del grupo
     --------------------------------------------------------------------------- */

  /** Normaliza para comparar sin acentos: "Antracita" tiene que salir escribiendo "antrac". */
  const fold = (text) =>
    text
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();

  /**
   * Recalcula que valores se ven en un grupo. Buscando no hay recorte: lo que quedo detras
   * del limite es justamente lo que se esta buscando.
   */
  const paintGroup = (group) => {
    if (!group) return;
    const list = group.querySelector('[data-filter-list]');
    if (!list) return;

    const input = group.querySelector('[data-filter-search]');
    const empty = group.querySelector('[data-filter-empty]');
    const query = input ? fold(input.value) : '';
    let matches = 0;

    for (const item of list.children) {
      const hit = query === '' || fold(item.dataset.filterLabel || '').includes(query);
      item.toggleAttribute('data-filter-nomatch', !hit);
      if (hit) matches += 1;
    }

    list.toggleAttribute('data-expanded', query !== '' || list.hasAttribute('data-user-expanded'));
    if (empty) empty.hidden = query === '' || matches > 0;
  };

  const setMoreState = (group, expanded) => {
    const list = group.querySelector('[data-filter-list]');
    const more = group.querySelector('[data-filter-more]');
    if (!list) return;
    list.toggleAttribute('data-user-expanded', expanded);
    if (more) {
      more.setAttribute('aria-expanded', String(expanded));
      more.textContent = expanded ? more.dataset.labelLess : more.dataset.labelMore;
    }
    paintGroup(group);
  };

  form.addEventListener('click', (event) => {
    const more = event.target.closest('[data-filter-more]');
    if (!more) return;
    const group = more.closest('[data-filter-key]');
    setMoreState(group, more.getAttribute('aria-expanded') !== 'true');
  });

  form.addEventListener('input', (event) => {
    const input = event.target.closest('[data-filter-search]');
    if (input) paintGroup(input.closest('[data-filter-key]'));
  });

  /* El buscador del grupo vive dentro del formulario: sin esto Enter aplicaria un estado
     que el cliente todavia no vio, porque la lista esta recortada. */
  form.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.closest('[data-filter-search]')) {
      event.preventDefault();
    }
  });

  /* ---------------------------------------------------------------------------
     Modal en mobile
     --------------------------------------------------------------------------- */

  const desktop = window.matchMedia('(min-width: 799px)');
  const toggle = document.querySelector('[data-filter-toggle]');
  const sidebar = document.querySelector('[data-sidebar]');

  if (dialog && toggle && sidebar && sidebar.parentElement) {
    const home = sidebar.parentElement;
    const dialogBody = dialog.querySelector('[data-filter-dialog-body]');

    /* Fuera de la seccion: asi las reglas de la barra plegada en mobile
       —`.shopify-section--collection-template .sidebar`— dejan de alcanzarla mientras esta
       abierta, sin tener que apagarlas con otro selector. */
    document.body.append(dialog);

    toggle.addEventListener('click', () => {
      dialogBody.append(sidebar);
      dialog.showModal();
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('scroll-locked');
    });

    dialog.addEventListener('close', () => {
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('scroll-locked');
      home.prepend(sidebar);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-dialog-close]')) dialog.close();
    });

    /* Safari todavia no soporta `closedby`, asi que ahi el clic en el fondo se mide a mano. */
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        const inside =
          event.clientY >= box.top &&
          event.clientY <= box.bottom &&
          event.clientX >= box.left &&
          event.clientX <= box.right;
        if (!inside) dialog.close();
      });
    }

    desktop.addEventListener('change', () => {
      if (desktop.matches && dialog.open) dialog.close();
    });
  }

  /* ---------------------------------------------------------------------------
     Filtrado en vivo
     --------------------------------------------------------------------------- */

  const live = Boolean(toolbar) && toolbar.dataset.liveFilter === 'true' && Boolean(sectionId) && Boolean(results);
  if (!live) return;

  const debounceMs = Number(toolbar.dataset.debounce) || 300;
  let timer = null;
  let controller = null;
  let seq = 0;

  /** Estado del formulario como query string. Los campos vacios no viajan. */
  const params = () => {
    const search = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') search.append(key, value);
    }
    if (sortEl && sortEl.value) search.set('sort_by', sortEl.value);
    return search.toString();
  };

  const setBusy = (busy) => {
    results.toggleAttribute('data-plp-loading', busy);
    results.setAttribute('aria-busy', String(busy));
  };

  const clearFilters = (scope, onlyType) => {
    for (const input of scope.querySelectorAll('input[name]')) {
      if (onlyType && input.type !== onlyType) continue;
      if (input.type === 'checkbox') input.checked = false;
      else if (input.type === 'number') input.value = '';
    }
  };

  const focusRef = () => {
    const el = document.activeElement;
    if (!el || !form.contains(el)) return null;
    if (el.name) return `input[name="${CSS.escape(el.name)}"][value="${CSS.escape(el.value)}"]`;
    const group = el.closest('[data-filter-key]');
    if (group && el.matches('[data-filter-search]')) {
      return `[data-filter-key="${CSS.escape(group.dataset.filterKey)}"] [data-filter-search]`;
    }
    return null;
  };

  /** Lo que el reemplazo del formulario borraria y el servidor no puede saber. */
  const snapshot = () => {
    const groups = [...form.querySelectorAll('[data-filter-key]')];
    return {
      keys: groups.map((group) => group.dataset.filterKey),
      open: groups.filter((group) => group.open).map((group) => group.dataset.filterKey),
      expanded: groups
        .filter((group) => group.querySelector('[data-filter-list][data-user-expanded]'))
        .map((group) => group.dataset.filterKey),
      queries: groups
        .map((group) => [group.dataset.filterKey, group.querySelector('[data-filter-search]')])
        .filter(([, input]) => input)
        .map(([key, input]) => [key, input.value]),
      focus: focusRef(),
    };
  };

  const restore = (state) => {
    const known = new Set(state.keys);
    for (const group of form.querySelectorAll('[data-filter-key]')) {
      const key = group.dataset.filterKey;
      /* Un grupo que no existia antes se deja como lo mando el servidor. */
      if (known.has(key)) group.open = state.open.includes(key);
      if (state.expanded.includes(key)) setMoreState(group, true);

      const entry = state.queries.find(([storedKey]) => storedKey === key);
      const input = group.querySelector('[data-filter-search]');
      if (input && entry && entry[1]) input.value = entry[1];
      paintGroup(group);
    }
    if (state.focus) {
      const target = form.querySelector(state.focus);
      if (target) target.focus({ preventScroll: true });
    }
  };

  /** Vuelve a atar lo que el theme ata por elemento y se perdio con el reemplazo. */
  const rehydrate = (scope) => {
    const settings = (window.PXUTheme && window.PXUTheme.theme_settings) || {};

    if (window.rimg && window.rimg.shopify && window.rimg.shopify.watch) {
      window.rimg.shopify.watch(scope);
    }
    if (window.PXUTheme && window.PXUTheme.currency && window.PXUTheme.currency.show_multiple_currencies) {
      window.currencyConverter.convertCurrencies();
    }
    if (window.productPage) window.productPage.init();
    /* El precio de la ficha no viene pintado: `runOptionSelector` es quien corre
       `priceUI.load` y le saca la clase `price-ui--loading`, que lo deja en
       `opacity: 0`. Sin esta llamada las fichas nuevas se quedan con el precio
       en el DOM pero invisible, y ya no se recupera solo. */
    if (window.productPage && window.jQuery) {
      window.productPage.runOptionSelector(window.jQuery(scope));
    }
    if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
    if (settings.quick_shop_enabled && window.quickShop) window.quickShop.init();
    if (window.hideNoScript) window.hideNoScript();
    if (window.videoFeature) window.videoFeature.init();
    if (settings.collection_secondary_image && window.imageFunctions) {
      window.imageFunctions.showSecondaryImage();
    }
    if (window.initCollectionInfiniteScroll) window.initCollectionInfiniteScroll();
    /* Los gestos de la fila de swatches van delegados en `document` y sobreviven al
       reemplazo, pero el degradado de borde y el cursor de arrastre salen de
       `data-swatch-overflow`, que se calcula midiendo: sin recalcular, una fila que
       desborda no avisa que tiene mas swatches al costado. */
    if (window.swatchScroll) window.swatchScroll.actualizarTodos();
  };

  const paint = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextForm = doc.querySelector('[data-faceted-filter-form]');
    const nextResults = doc.querySelector('[data-plp-results]');

    if (nextForm) {
      const state = snapshot();
      form.innerHTML = nextForm.innerHTML;
      /* El acordeon del theme se ata por bloque: sin re-atarlo los grupos pierden la
         animacion y el candado que los deja siempre abiertos en desktop. */
      if (window.sidebarAccordions) window.sidebarAccordions.init();
      restore(state);
    }

    /* El "limpiar todo" y el contador del modal viven fuera del formulario, asi que el
       reemplazo no los alcanza y quedarian congelados. Los dos se derivan de los chips
       activos, que es la misma cuenta que emite Liquid. */
    const activeFilters = form.querySelectorAll('[data-remove-filter]').length;
    const hasActiveFilters = activeFilters > 0;

    for (const badge of document.querySelectorAll('[data-filter-count]')) {
      badge.textContent = String(activeFilters);
      badge.hidden = !hasActiveFilters;
      const label = doc.querySelector('[data-filter-count]');
      if (label) badge.setAttribute('aria-label', label.getAttribute('aria-label') || '');
    }

    for (const clear of document.querySelectorAll('[data-clear-all]')) {
      if (form.contains(clear)) continue;
      clear.classList.toggle('is-inert', !hasActiveFilters);
      if (hasActiveFilters) {
        clear.removeAttribute('aria-disabled');
        clear.removeAttribute('tabindex');
      } else {
        clear.setAttribute('aria-disabled', 'true');
        clear.setAttribute('tabindex', '-1');
      }
    }

    const pairs = [
      ['[data-product-count]', doc.querySelector('[data-product-count]')],
      ['[data-product-count-action]', doc.querySelector('[data-product-count-action]')],
    ];
    for (const [selector, source] of pairs) {
      const target = document.querySelector(selector);
      if (target && source) target.textContent = source.textContent;
    }

    const nextSort = doc.querySelector('[data-sort-by]');
    if (sortEl && nextSort && sortEl.value !== nextSort.value) sortEl.value = nextSort.value;
    const nextSortLabel = doc.querySelector('.ff-sort__value');
    if (sortLabel && nextSortLabel) sortLabel.textContent = nextSortLabel.textContent;

    if (nextResults) {
      results.innerHTML = nextResults.innerHTML;
      rehydrate(results);
    }
  };

  /**
   * @param {{push?: boolean, search?: string|null}} options
   *   `search` nulo significa "leer el formulario"; con valor se usa tal cual, que es lo que
   *   necesita el boton atras: ahi la URL manda y el formulario todavia no la refleja.
   */
  const run = async ({ push = true, search = null } = {}) => {
    const fromForm = search === null;
    const query = fromForm ? params() : search;
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    const endpoint = `${window.location.pathname}?${query ? `${query}&` : ''}sections=${encodeURIComponent(sectionId)}`;

    if (controller) controller.abort();
    controller = new AbortController();
    const id = ++seq;

    let html;
    try {
      const response = await fetch(endpoint, { signal: controller.signal });
      if (!response.ok) throw new Error(`sections request failed with ${response.status}`);
      const payload = await response.json();
      html = payload[sectionId];
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Nunca dejar la grilla vieja debajo de un filtro tildado: se navega y el servidor
         resuelve. */
      window.location.assign(url);
      return;
    }

    if (id !== seq) return;
    /* El cliente siguio tocando: la corrida que ya esta agendada tiene el estado bueno. */
    if (fromForm && params() !== query) return;

    paint(html);
    if (push) window.history.pushState({ plpFilter: true }, '', url);
    setBusy(false);
  };

  const schedule = () => {
    clearTimeout(timer);
    setBusy(true);
    timer = setTimeout(() => run(), debounceMs);
  };

  form.addEventListener('change', (event) => {
    /* Sin `name` no es un filtro: el buscador del grupo dispara `change` al perder el foco. */
    if (event.target.name) schedule();
  });

  /* Los campos de precio se escriben a mano; con `input` no hay que esperar el blur. */
  form.addEventListener('input', (event) => {
    if (event.target.type === 'number' && event.target.name) schedule();
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    clearTimeout(timer);
    setBusy(true);
    run();
  });

  if (sortEl) sortEl.addEventListener('change', schedule);

  form.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-remove-filter], [data-clear-group]');
    if (!trigger) return;
    event.preventDefault();

    if (trigger.hasAttribute('data-clear-group')) {
      clearFilters(trigger.closest('[data-filter-key]'));
    } else if (trigger.hasAttribute('data-filter-price')) {
      clearFilters(form, 'number');
    } else {
      const input = form.querySelector(
        `input[name="${CSS.escape(trigger.dataset.filterParam)}"][value="${CSS.escape(trigger.dataset.filterValue)}"]`
      );
      if (input) input.checked = false;
    }
    schedule();
  });

  /* Delegado en el documento porque el "limpiar todo" del modal vive fuera del formulario. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-clear-all]');
    if (!trigger) return;
    event.preventDefault();
    if (trigger.getAttribute('aria-disabled') === 'true') return;
    clearFilters(form);
    schedule();
  });

  window.addEventListener('popstate', () => {
    clearTimeout(timer);
    setBusy(true);
    run({ push: false, search: window.location.search.replace(/^\?/, '') });
  });
})();
